import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Ticket } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, brl } from "./api";

const inputCls = "w-full bg-white border border-military/20 rounded-lg px-3 py-2.5 text-sm text-military-dark focus:outline-none focus:border-military";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] font-semibold text-military-dark/50 mb-1.5";

const empty = {
  code: "", type: "percent", value: "", start_date: "", end_date: "", min_purchase: "",
  usage_limit: "", per_client_limit: "", product_slugs: [], first_purchase_only: false, active: true,
};

const describe = (c) => {
  const parts = [c.type === "percent" ? `${c.value}% OFF` : `${brl(c.value)} OFF`];
  if (c.min_purchase) parts.push(`mín. ${brl(c.min_purchase)}`);
  if (c.product_slugs?.length) parts.push(`${c.product_slugs.length} produto(s)`);
  else parts.push("toda a loja");
  if (c.first_purchase_only) parts.push("1ª compra");
  return parts.join(" · ");
};

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleScope = (slug) => {
    const cur = editing.product_slugs || [];
    setEditing({ ...editing, product_slugs: cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug] });
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      code: editing.code.toUpperCase().trim(),
      value: Number(editing.value),
      start_date: editing.start_date || null,
      end_date: editing.end_date || null,
      min_purchase: editing.min_purchase === "" ? null : Number(editing.min_purchase),
      usage_limit: editing.usage_limit === "" ? null : Number(editing.usage_limit),
      per_client_limit: editing.per_client_limit === "" ? null : Number(editing.per_client_limit),
    };
    try {
      if (editing._original) await api.put(`/admin/coupons/${editing._original}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success("Cupom salvo");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir o cupom "${c.code}"?`)) return;
    await api.delete(`/admin/coupons/${c.code}`).catch(() => {});
    toast.success("Cupom excluído");
    load();
  };

  return (
    <div data-testid="admin-coupons">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl md:text-4xl text-military-dark">Cupons de desconto</h1>
        <button data-testid="add-coupon-button" onClick={() => setEditing({ ...empty })} className="flex items-center gap-2 rounded-full bg-military text-offwhite px-5 py-2.5 text-xs uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors">
          <Plus size={14} /> Criar cupom
        </button>
      </div>

      <div className="space-y-3">
        {coupons.map((c) => (
          <div key={c.code} data-testid={`coupon-${c.code}`} className="bg-white border border-military/10 rounded-lg p-4 flex flex-wrap items-center gap-4">
            <Ticket size={18} className="text-military shrink-0" />
            <div className="flex-1 min-w-[180px]">
              <p className="font-semibold text-military-dark text-sm font-mono">{c.code}</p>
              <p className="text-xs text-military-dark/50">{describe(c)}</p>
              <p className="text-xs text-military-dark/40 mt-0.5">
                {c.start_date || c.end_date ? `válido ${c.start_date || "…"} → ${c.end_date || "…"}` : "sem validade"} · usado {c.uses || 0}x{c.usage_limit ? ` de ${c.usage_limit}` : ""}
              </p>
            </div>
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full ${c.active ? "bg-military/10 text-military" : "bg-military-dark/10 text-military-dark/50"}`}>
              {c.active ? "Ativo" : "Inativo"}
            </span>
            <div className="flex gap-1">
              <button data-testid={`edit-coupon-${c.code}`} onClick={() => setEditing({ ...c, _original: c.code, start_date: c.start_date || "", end_date: c.end_date || "", min_purchase: c.min_purchase ?? "", usage_limit: c.usage_limit ?? "", per_client_limit: c.per_client_limit ?? "", product_slugs: c.product_slugs || [] })} className="p-2 rounded-lg hover:bg-baby transition-colors"><Pencil size={14} /></button>
              <button data-testid={`delete-coupon-${c.code}`} onClick={() => remove(c)} className="p-2 rounded-lg hover:bg-red-50 text-red-700 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {coupons.length === 0 && <p className="text-sm text-military-dark/40">Nenhum cupom criado ainda.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-military-dark/40 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={() => setEditing(null)}>
          <form data-testid="coupon-form" onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-offwhite w-full md:max-w-xl max-h-[90vh] overflow-y-auto rounded-t-lg md:rounded-lg p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-military-dark">{editing._original ? "Editar cupom" : "Novo cupom"}</h2>
              <button type="button" onClick={() => setEditing(null)} className="p-2 rounded-full hover:bg-baby"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Código</label><input data-testid="cf-code" required value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} placeholder="BEMVINDO10" className={inputCls + " font-mono"} /></div>
              <div>
                <label className={labelCls}>Tipo de desconto</label>
                <select data-testid="cf-type" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className={inputCls}>
                  <option value="percent">Percentual (%)</option>
                  <option value="fixed">Valor fixo (R$)</option>
                </select>
              </div>
              <div><label className={labelCls}>{editing.type === "percent" ? "Percentual (%)" : "Valor (R$)"}</label><input data-testid="cf-value" required type="number" step="0.01" min="0" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Compra mínima (R$ — opcional)</label><input data-testid="cf-min" type="number" step="0.01" min="0" value={editing.min_purchase} onChange={(e) => setEditing({ ...editing, min_purchase: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Início (opcional)</label><input data-testid="cf-start" type="date" value={editing.start_date} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Validade (opcional)</label><input data-testid="cf-end" type="date" value={editing.end_date} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Limite total de usos (opcional)</label><input data-testid="cf-usage-limit" type="number" min="1" value={editing.usage_limit} onChange={(e) => setEditing({ ...editing, usage_limit: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Limite por cliente (opcional)</label><input data-testid="cf-per-client" type="number" min="1" value={editing.per_client_limit} onChange={(e) => setEditing({ ...editing, per_client_limit: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="mt-5">
              <label className={labelCls}>Aplicação — deixe tudo desmarcado para valer na loja toda</label>
              <div className="max-h-36 overflow-y-auto bg-white border border-military/15 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="cf-products">
                {products.map((p) => (
                  <label key={p.slug} className="flex items-center gap-2 text-xs text-military-dark cursor-pointer">
                    <input type="checkbox" checked={(editing.product_slugs || []).includes(p.slug)} onChange={() => toggleScope(p.slug)} className="w-3.5 h-3.5 accent-[#4E5D46]" />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-6 mt-5">
              <label className="flex items-center gap-2 text-sm text-military-dark cursor-pointer">
                <input data-testid="cf-first-purchase" type="checkbox" checked={!!editing.first_purchase_only} onChange={(e) => setEditing({ ...editing, first_purchase_only: e.target.checked })} className="w-4 h-4 accent-[#4E5D46]" />
                Apenas primeira compra
              </label>
              <label className="flex items-center gap-2 text-sm text-military-dark cursor-pointer">
                <input data-testid="cf-active" type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="w-4 h-4 accent-[#4E5D46]" />
                Ativo
              </label>
            </div>
            <button data-testid="coupon-form-save" type="submit" className="mt-6 w-full rounded-full bg-military-dark text-offwhite py-3.5 text-sm uppercase tracking-widest font-semibold hover:bg-military transition-colors">
              Salvar cupom
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
